import { Button, Card, Form, Grid, Input, Message, Select, Space, Table } from "@arco-design/web-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InvAdjustmentOrderAPI, InventoryAPI } from "@/apis/material";
import { InputNumber } from "@/components/Numeric";
import { Currency } from "@/components/Numeric";
import { Date } from "@/components/Date";
import WarehouseSelect from "@/components/WarehouseSelect";
import AccountSelect from "@/components/AccountSelect";
import { ItemSelect } from "@/components/ItemSelect";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { AccountCategory } from "@/enums/accounting";
import { InvAdjustmentOrderType } from "@/enums/material";
import Decimal from "decimal.js";
import _ from "lodash";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;

const newItemRow = () => ({
    key: _.uniqueId("row_"),
    itemId: undefined,
    itemName: "",
    description: "",
    locationId: undefined,
    locationName: "",
    lot: undefined,
    expiredDate: "",
    qty: null,
    cost: null,
    qtyAdjustment: null,
    newQty: null,
    valueAdjustment: null,
});

export const InvAdjustmentAdd = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [warehouseId, setWarehouseId] = useState();
    const [rows, setRows] = useState([newItemRow()]);
    const [inventoryMap, setInventoryMap] = useState({});
    
    const adjustmentValue = _.reduce(rows, (sum, row) => {
        return new Decimal(sum).plus(row.valueAdjustment ?? 0).toNumber();
    }, 0);

    const onWarehouseChange = useCallback(async (val) => {
        setWarehouseId(val);
        form.setFieldValue("warehouseId", val);
        // Clear inventory data and reset rows when warehouse changes
        setRows(prev => {
            const newRows = prev.map(row => ({
                ...row,
                locationId: undefined,
                locationName: "",
                lot: undefined,
                expiredDate: "",
                qty: null,
                cost: null,
                qtyAdjustment: null,
                newQty: null,
                valueAdjustment: null,
            }));

            // Re-fetch inventory data for rows that already have an item selected
            newRows.forEach((row, index) => {
                if (row.itemId && val) {
                    InventoryAPI.itemInventories([row.itemId])
                        .then(inventories => {
                            setInventoryMap(prevMap => ({
                                ...prevMap,
                                [index]: _.filter(inventories, { warehouseId: val }),
                            }));
                        })
                        .catch(() => {
                            setInventoryMap(prevMap => ({
                                ...prevMap,
                                [index]: [],
                            }));
                        });
                } else {
                    setInventoryMap(prevMap => ({
                        ...prevMap,
                        [index]: [],
                    }));
                }
            });

            return newRows;
        });
    }, [form]);

    const onItemSelect = useCallback(async (index, item) => {
        const itemId = item?.value;
        setRows(prev => {
            const newRows = [...prev];
            newRows[index] = {
                ...newRows[index],
                itemId,
                itemName: item?.itemName || "",
                description: item?.description || "",
                lot: undefined,
                expiredDate: "",
                locationId: undefined,
                locationName: "",
                qty: null,
                cost: null,
                qtyAdjustment: null,
                newQty: null,
                valueAdjustment: null,
            }; 
            return newRows;
        });
    
        if (itemId && warehouseId) {
            try {
                const inventories = await InventoryAPI.itemInventories([itemId]);
                setInventoryMap(prev => ({
                    ...prev,
                    [index]: _.filter(inventories, { warehouseId }),
                }));
            } catch (e) {
                setInventoryMap(prev => ({
                    ...prev,
                    [index]: [],
                }));
            }
        } else {
            setInventoryMap(prev => ({
                ...prev,
                [index]: [],
            }));
        }
    }, [warehouseId]);

    const getLocationsForRow = useCallback((index) => {
        const inventories = inventoryMap[index] || [];
        return _.uniqBy(
            _.filter(_.map(inventories, inv => ({
                value: inv.locationId,
                label: inv.locationName || "-",
            })), loc => loc.value != null),
            'value'
        );
    }, [inventoryMap]);

    const getLotsForRow = useCallback((index, locationId) => {
        const inventories = inventoryMap[index] || [];
        let filtered = inventories;
        if (locationId) {
            filtered = inventories.filter(inv => inv.locationId === locationId);
        }
        return _.filter(_.map(filtered, inv => ({
            value: inv.lot,
            label: inv.lot || "-",
            locationId: inv.locationId,
            locationName: inv.locationName,
            quantity: inv.onHandQty,
            cost: inv.rate,
            expiredDate: inv.expiredDate,
        })), lot => lot.value != null && lot.value !== "");
    }, [inventoryMap]);

    const onLocationSelect = useCallback((index, val) => {
        const inventories = inventoryMap[index] || [];
        const inv = _.find(inventories, { locationId: val });
        setRows(prev => {
            const newRows = [...prev];
            newRows[index] = {
                ...newRows[index],
                locationId: val,
                locationName: inv?.locationName || "",
                lot: undefined,
                expiredDate: "",
                qty: null,
                cost: null,
                qtyAdjustment: null,
                newQty: null,
                valueAdjustment: null,
            };
            return newRows;
        });
    }, [inventoryMap]);

    const onLotSelect = useCallback((index, lot) => {
        const inventories = inventoryMap[index] || [];
        setRows(prev => {
            const row = prev[index];
            let match;
            if (row?.locationId) {
                match = inventories.find(inv => inv.lot === lot && inv.locationId === row.locationId);
            }
            if (!match) {
                match = inventories.find(inv => inv.lot === lot);
            }
            if (match) {
                const qty = match.onHandQty ?? 0;
                const cost = match.rate ?? 0;
                const qtyAdj = row.qtyAdjustment ?? 0;
                const newRows = [...prev];
                newRows[index] = {
                    ...row,
                    lot,
                    locationId: match.locationId,
                    locationName: match.locationName,
                    expiredDate: match.expiredDate || "",
                    qty,
                    cost,
                    newQty: new Decimal(qty).plus(qtyAdj).toNumber(),
                    valueAdjustment: new Decimal(qtyAdj).times(cost).toNumber(),
                };
                return newRows;
            }
            const newRows = [...prev];
            newRows[index] = { ...row, lot };
            return newRows;
        });
    }, [inventoryMap]);

    const onQtyAdjChange = useCallback((index, val) => {
        setRows(prev => {
            const newRows = [...prev];
            const row = newRows[index];
            const qty = row.qty ?? 0;
            const cost = row.cost ?? 0;
            const qtyAdj = val ?? 0;
            newRows[index] = {
                ...row,
                qtyAdjustment: val,
                newQty: new Decimal(qty).plus(qtyAdj).toNumber(),
                valueAdjustment: new Decimal(qtyAdj).times(cost).toNumber(),
            };
            return newRows;
        });
    }, []);

    const addRow = useCallback(() => {
        setRows(prev => [...prev, newItemRow()]);
    }, []);

    const removeRow = useCallback((index) => {
        setRows(prev => {
            const newRows = [...prev];
            newRows.splice(index, 1);
            if (newRows.length === 0) newRows.push(newItemRow());
            return newRows;
        });
        setInventoryMap(prev => {
            const newMap = {};
            Object.keys(prev).forEach(key => {
                const k = parseInt(key);
                if (k < index) {
                    newMap[k] = prev[k];
                } else if (k > index) {
                    newMap[k - 1] = prev[k];
                }
            });
            return newMap;
        });
    }, []);

    const onSubmit = async () => {
        try {
            const values = await form.validate();

            for (let i = 0; i < rows.length; i++) {
                if (!rows[i].itemId) {
                    Message.error(`Row ${i + 1}: Item is required`);
                    return;
                }
                if (rows[i].qtyAdjustment == null) {
                    Message.error(`Row ${i + 1}: Qty Adjustment is required`);
                    return;
                }
                if (rows[i].newQty != null && rows[i].newQty < 0) {
                    Message.error(`Row ${i + 1}: New Qty cannot be negative`);
                    return;
                }
            }

            const params = {
                orderType: InvAdjustmentOrderType.Adjustment,
                accountId: values.accountId,
                warehouseId: values.warehouseId,
                notes: values.notes,
                items: _.map(rows, row => ({
                    itemId: row.itemId,
                    itemName: row.itemName,
                    itemDescription: row.description,
                    locationId: row.locationId,
                    locationName: row.locationName,
                    lot: row.lot || null,
                    expiredDate: row.expiredDate || null,
                    qty: row.qty,
                    cost: row.cost,
                    qtyAdjustment: row.qtyAdjustment,
                    newQty: row.newQty,
                    valueAdjustment: row.valueAdjustment,
                })),
            };

            setLoading(true);
            await InvAdjustmentOrderAPI.add(params);
            Message.success("Inventory adjustment created successfully");
            navigate("/workspace/material/inventory_adjustment");
        } catch (error) {
            if (error.name !== "ValidationError") {
                Message.error(error.message || "Failed to create inventory adjustment");
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>), dataIndex: "itemId", width: 280,
            render: (col, record, index) => (
                <ItemSelect
                    value={record.itemId}
                    onSelectItem={(item) => onItemSelect(index, item)}
                    style={{ width: "100%" }} />
            ),
        },
        {
            title: "Location", dataIndex: "locationId", width: 160,
            render: (col, record, index) => {
                const locations = getLocationsForRow(index);
                return (
                    <Select showSearch allowClear placeholder="Select Location"
                        value={record.locationId}
                        onChange={(val) => onLocationSelect(index, val)}
                        style={{ width: '100%' }}>
                        {_.map(locations, loc => (
                            <Select.Option key={loc.value} value={loc.value}>{loc.label}</Select.Option>
                        ))}
                    </Select>
                );
            },
        },
        {
            title: "Lot#", dataIndex: "lot", width: 140,
            render: (col, record, index) => {
                const lots = getLotsForRow(index, record.locationId);
                return (
                    <Select showSearch allowClear placeholder="Select Lot"
                        value={record.lot}
                        onChange={(val) => onLotSelect(index, val)}
                        style={{ width: '100%' }}>
                        {_.map(lots, lot => (
                            <Select.Option key={lot.value} value={lot.value}>{lot.label}</Select.Option>
                        ))}
                    </Select>
                );
            },
        },
        {
            title: "Expiration", dataIndex: "expiredDate", width: 110, align: "center",
            render: (col, record) => record.expiredDate ? <Date value={record.expiredDate} /> : "",
        },
        {
            title: "Qty", dataIndex: "qty", width: 80, align: "right",
            render: (col, record) => <span>{record.qty != null ? record.qty : ""}</span>,
        },
        {
            title: "Cost", dataIndex: "cost", width: 100, align: "right",
            render: (col, record) => <span>{record.cost != null ? <Currency value={record.cost} /> : ""}</span>,
        },
        {
            title: "Qty Adjustment", dataIndex: "qtyAdjustment", width: 140,
            render: (col, record, index) => (
                <InputNumber placeholder="Qty Adj" style={{ width: "100%" }}
                    value={record.qtyAdjustment}
                    onChange={(val) => onQtyAdjChange(index, val)} />
            ),
        },
        {
            title: "New Qty", dataIndex: "newQty", width: 100, align: "right",
            render: (col, record) => {
                const val = record.newQty;
                return <span style={{ color: val < 0 ? "red" : undefined }}>{val != null ? val : ""}</span>;
            },
        },
        {
            title: "Value Adjustment", dataIndex: "valueAdjustment", width: 140, align: "right",
            render: (col, record) => <span>{record.valueAdjustment != null ? <Currency value={record.valueAdjustment} /> : ""}</span>,
        },
        {
            title: "", width: 50, align: "center",
            render: (col, record, index) => (
                <Button type="text" icon={<IconDelete />} status="danger" onClick={() => removeRow(index)} />
            ),
        },
    ];

    return (
        <Card title="Add Inventory Adjustment" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Submit</Button>
                <Button onClick={() => navigate("/workspace/material/inventory_adjustment")}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon>
                <Row gutter={24}>
                    <Col span={12}>
                        <FormItem field="accountId" label="Inventory Adj. Account" required
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}
                            rules={[{ required: true, message: "Account is required" }]}>
                            <AccountSelect category={AccountCategory.Expenses} showSearch placeholder="Select Account" />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem field="warehouseId" label="Warehouse" required
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}
                            rules={[{ required: true, message: "Warehouse is required" }]}>
                            <WarehouseSelect includeUnavailable placeholder="Select Warehouse" onChange={onWarehouseChange} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <FormItem field="notes" label="Notes" required
                            labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}
                            rules={[{ required: true, message: "Notes is required" }]}>
                            <TextArea placeholder="Notes" />
                        </FormItem>
                    </Col>
                </Row>
            </Form>

            <Table rowKey="key"
                columns={columns}
                data={rows}
                pagination={false}
                footer={() => (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Button type="outline" size="small" shape="round" status="success"
                            icon={<IconPlus />} onClick={addRow}>
                            Add Row
                        </Button>
                        <span>Total Value Adjustment: <Currency value={adjustmentValue} /></span>
                    </div>
                )}
            />
        </Card>
    );
};
